import { Tabs, IconButton, LoadingSkeleton } from "@narmi/design_system";
import { useCallback, useMemo, useState } from "react";
import { useLocalization } from "@fluent/react";
import { DateTime } from "luxon";

import BorderCard from "../../BorderCard";
import { useAccountsContext } from "../../contexts/AccountContext";
import DateFilterInput from "../RecentTransactionsModule/DateFilterInput";
import RangeFilterChip from "../RecentTransactionsModule/RangeFilterChip";
import AccountFilterContent from "./AccountFilterContent/AccountFilterContent";
import CategoryBreakdown from "./CategoryBreakdown/CategoryBreakdown";
import useYearTransactions from "./useYearTransactions";
import styles from "./PfmContainer.module.scss";

const PfmContainer = () => {
  const { l10n } = useLocalization();
  const { accounts, externalAccounts } = useAccountsContext();
  const { isLoading, transactions: txns } = useYearTransactions(
    [...accounts, ...externalAccounts],
    "all"
  );
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [appliedAccountIds, setAppliedAccountIds] =
    useState<Set<string> | null>(null);

  // `DateFilterInput` emits 'YYYY-MM-DD'.
  const applyDateRange = useCallback((from: string, to: string) => {
    setStartDate(from);
    setEndDate(to);
  }, []);

  // Blocks Apply (and surfaces an error on the "to" field) rather than
  // silently clearing the end date when the range is inverted.
  const validateDateRange = useCallback(
    (from: string, to: string) =>
      !!(from && to && DateTime.fromISO(from) > DateTime.fromISO(to)),
    []
  );

  const openAccountDrawer = useCallback(() => {
    setIsAccountDrawerOpen(true);
  }, []);

  // Dismissing (X, backdrop, Escape) discards the draft; only Apply commits.
  const closeAccountDrawer = useCallback(
    () => setIsAccountDrawerOpen(false),
    []
  );

  const applyAccountFilter = useCallback(
    (draftAccountIds: Set<string> | null) => {
      setAppliedAccountIds(draftAccountIds);
      setIsAccountDrawerOpen(false);
    },
    []
  );

  const filteredTxns = useMemo(() => {
    if (!startDate && !endDate && !appliedAccountIds) return txns;
    return txns.filter((t) => {
      // An empty selection matches nothing, by design.
      if (appliedAccountIds && !appliedAccountIds.has(t.account_id ?? "")) {
        return false;
      }
      if (!startDate && !endDate) return true;
      const d = t.settled_at?.slice(0, 10);
      if (!d) return false;
      return (!startDate || d >= startDate) && (!endDate || d <= endDate);
    });
  }, [txns, startDate, endDate, appliedAccountIds]);

  const hasNoFilteredTxns = txns.length > 0 && filteredTxns.length === 0;

  return (
    <BorderCard
      title={l10n.getString("pfm-title")}
      isDrawerOpen={isAccountDrawerOpen}
      onDrawerDismiss={closeAccountDrawer}
      drawerChildren={
        <AccountFilterContent
          accounts={accounts}
          selectedIds={appliedAccountIds}
          isOpen={isAccountDrawerOpen}
          onApply={applyAccountFilter}
        />
      }
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerControls}>
            <IconButton
              onClick={openAccountDrawer}
              name="filter"
              disabled={isAccountDrawerOpen}
              label={l10n.getString("pfm-accounts-open-filter")}
            />
            <div className={styles.dateRange}>
              <RangeFilterChip
                label={l10n.getString("recent-transactions-filter-date")}
                valueFrom={startDate}
                valueTo={endDate}
                onApply={applyDateRange}
                validate={validateDateRange}
                defaultPendingTo={DateTime.now().toISODate() ?? undefined}
                renderFrom={(value, onChange) => (
                  <DateFilterInput
                    label={l10n.getString(
                      "recent-transactions-date-filter-from"
                    )}
                    initialValue={value}
                    onChange={onChange}
                  />
                )}
                renderTo={(value, onChange, hasError, pendingFrom) => (
                  <DateFilterInput
                    label={l10n.getString("recent-transactions-date-filter-to")}
                    initialValue={value}
                    minDate={pendingFrom}
                    onChange={onChange}
                    error={
                      hasError
                        ? l10n.getString(
                            "recent-transactions-date-filter-error"
                          )
                        : undefined
                    }
                  />
                )}
              />
            </div>
          </div>
          <Tabs selectedIndex={tab} onTabChange={setTab} hasBorder={false}>
            <Tabs.List xPadding="l">
              <Tabs.Tab
                label={l10n.getString("pfm-tab-spending")}
                tabId="spending"
              />
              <Tabs.Tab
                label={l10n.getString("pfm-tab-income")}
                tabId="income"
              />
            </Tabs.List>
          </Tabs>
        </div>
        <div className={styles.breakdownContainer}>
          <LoadingSkeleton isLoading={isLoading}>
            {tab === 0 ? (
              <CategoryBreakdown
                transactions={filteredTxns}
                direction="debit"
                heading={l10n.getString("pfm-heading-spending")}
                centerLabel={l10n.getString("pfm-center-label-spending")}
                emptyMessage={
                  hasNoFilteredTxns
                    ? l10n.getString("pfm-empty-filtered")
                    : l10n.getString("pfm-empty-spending")
                }
              />
            ) : (
              <CategoryBreakdown
                transactions={filteredTxns}
                direction="credit"
                heading={l10n.getString("pfm-heading-income")}
                centerLabel={l10n.getString("pfm-center-label-income")}
                emptyMessage={
                  hasNoFilteredTxns
                    ? l10n.getString("pfm-empty-filtered")
                    : l10n.getString("pfm-empty-income")
                }
              />
            )}
          </LoadingSkeleton>
        </div>
      </div>
    </BorderCard>
  );
};

export default PfmContainer;
